import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { KnowledgeBaseWorkspacesDto } from '@inference.engine/assets-api-client/web';
import { useAuth } from '@/entities/user';
import { FormError, KnowledgeBaseFormData, KnowledgeBaseDto } from '@/features/knowledge-bases';
import type { KnowledgeBaseModalType } from '@/features/knowledge-bases/model/knowledge-base-modal.store.types';

/**
 * Hook for knowledge base form state and validation
 * Focused responsibility: Form logic only
 */
const DEFAULT_FORM_DATA: KnowledgeBaseFormData = {
  title: '',
  description: '',
  spaceVisibility: null,
  workspaceIds: [],
  id: '',
};

export interface UseKnowledgeBaseFormOptions {
  /** Reads a shared KB as `workspaces` rather than as the single-workspace shorthand. */
  canSelectWorkspaces?: boolean;
  /** The workspaces a KB is shared with, loaded separately: the summary only carries the first. */
  scope?: KnowledgeBaseWorkspacesDto;
}

function seedFormData(
  modalType: KnowledgeBaseModalType | undefined,
  knowledgeBase: KnowledgeBaseDto | undefined,
  { canSelectWorkspaces, scope }: UseKnowledgeBaseFormOptions,
): KnowledgeBaseFormData {
  // Delete seeds from the same record as edit: the confirmation is reached from
  // the edit form, and it needs the id to delete and the name to show.
  if ((modalType === 'edit' || modalType === 'delete') && knowledgeBase) {
    /* eslint-disable i18next/no-literal-string -- visibility scope wire enum values */
    const sharedWithWorkspaces = canSelectWorkspaces
      ? (scope?.visibility ?? knowledgeBase.visibility) === 'workspaces'
      : !!knowledgeBase.workspaceId;
    const visibility = knowledgeBase.isInternal
      ? 'internal'
      : sharedWithWorkspaces
        ? canSelectWorkspaces
          ? 'workspaces'
          : 'workspace'
        : 'organization';
    /* eslint-enable i18next/no-literal-string */
    return {
      title: knowledgeBase.name || '',
      description: knowledgeBase.description || '',
      spaceVisibility: visibility,
      workspaceIds: scope?.workspaceIds ?? [],
      id: knowledgeBase.id,
    };
  }
  return DEFAULT_FORM_DATA;
}
export const useKnowledgeBaseForm = (
  modalType?: KnowledgeBaseModalType,
  knowledgeBase?: KnowledgeBaseDto,
  options: UseKnowledgeBaseFormOptions = {},
) => {
  const { t } = useTranslation('knowledge');
  const { canSelectWorkspaces, scope } = options;
  const user = useAuth((state) => state.signedInUser.value);

  // (Rule 1 derived-inline): seed `formData` from props via
  // lazy initializer, then reconcile during render whenever the (modalType,
  // knowledgeBase.id) identity changes. Consumers that mount a fresh instance
  // per open (via `key`) will hit the initializer branch and skip the
  // reconciliation entirely.
  const [formData, setFormData] = useState<KnowledgeBaseFormData>(() =>
    seedFormData(modalType, knowledgeBase, options),
  );
  const [lastFormKey, setLastFormKey] = useState<string>(
    () => `${modalType ?? ''}|${knowledgeBase?.id ?? ''}`,
  );
  const nextFormKey = `${modalType ?? ''}|${knowledgeBase?.id ?? ''}`;
  if (nextFormKey !== lastFormKey) {
    setLastFormKey(nextFormKey);
    setFormData(seedFormData(modalType, knowledgeBase, options));
  }

  // The scope arrives after the form is seeded. Only the fields it owns are filled in, so a title
  // typed in the meantime survives.
  const [seededScope, setSeededScope] = useState<KnowledgeBaseWorkspacesDto | undefined>(scope);
  if (scope && scope !== seededScope) {
    setSeededScope(scope);
    setFormData((current) =>
      current.id === knowledgeBase?.id && current.spaceVisibility !== 'internal'
        ? {
            ...current,
            spaceVisibility: scope.visibility === 'workspaces' ? 'workspaces' : 'organization',
            workspaceIds: scope.workspaceIds,
          }
        : current,
    );
  }

  // Pure validation function for form fields
  const validateFormFields = useCallback(
    (data: KnowledgeBaseFormData): FormError[] => {
      const errors: FormError[] = [];

      // Title validation
      if (!data.title.trim()) {
        errors.push({
          title: t('knowledgeBases.useKnowledgeBaseForm.titleIsRequired'),
          field: 'title',
          severity: 'info',
        });
      } else if (data.title.length > 50) {
        errors.push({
          title: t('knowledgeBases.useKnowledgeBaseForm.titleMustBe50Characters'),
          field: 'title',
          severity: 'info',
        });
      }

      // Description validation
      if (!data.description.trim()) {
        errors.push({
          title: t('knowledgeBases.useKnowledgeBaseForm.descriptionIsRequired'),
          field: 'description',
          severity: 'info',
        });
      } else if (data.description.length > 200) {
        errors.push({
          title: t('knowledgeBases.useKnowledgeBaseForm.descriptionMustBe200Characters'),
          field: 'description',
          severity: 'info',
        });
      }

      return errors;
    },
    [t],
  );

  // System validation (organization, workspace, etc.)
  const validateSystemRequirements = useCallback((): FormError[] => {
    const errors: FormError[] = [];

    if (!user?.organization.id) {
      errors.push({
        field: 'general',
        severity: 'error',
        title: t('knowledgeBases.useKnowledgeBaseForm.organizationInformationIsMissingPlease'),
      });
    }

    return errors;
  }, [t, user?.organization.id]);

  // Real-time form validation (only field validation, not system checks)
  const fieldErrors = useMemo(() => {
    return validateFormFields(formData);
  }, [formData, validateFormFields]);

  // Form validity for UI (enables/disables submit button)
  const isFormValid = useMemo(() => {
    const hasRequiredFields = formData.title.trim() && formData.description.trim();
    const hasFieldErrors = fieldErrors.length > 0;
    const hasOrganization = !!user?.organization.id;
    // A KB shared with no workspaces would be visible to nobody, and the API refuses it.
    const hasWorkspaces =
      !canSelectWorkspaces ||
      formData.spaceVisibility !== 'workspaces' ||
      formData.workspaceIds.length > 0;

    return Boolean(hasRequiredFields && !hasFieldErrors && hasOrganization && hasWorkspaces);
  }, [
    formData.title,
    formData.description,
    formData.spaceVisibility,
    formData.workspaceIds,
    canSelectWorkspaces,
    fieldErrors,
    user?.organization.id,
  ]);

  // Complete validation for submit (includes system checks)
  const validateForSubmit = useCallback(
    (data: KnowledgeBaseFormData): FormError[] => {
      const fieldValidationErrors = validateFormFields(data);
      const systemValidationErrors = validateSystemRequirements();

      return [...fieldValidationErrors, ...systemValidationErrors];
    },
    [validateFormFields, validateSystemRequirements],
  );

  // Get error for a specific field
  const getFieldError = useCallback(
    (fieldName: string): string | undefined => {
      return fieldErrors.find((error) => error.field === fieldName)?.title;
    },
    [fieldErrors],
  );

  return {
    // Form state
    formData,
    setFormData,

    // Validation
    fieldErrors, // Real-time errors for display
    isFormValid, // For submit button state
    getFieldError, // Helper for field-specific errors
    validateForSubmit, // Complete validation for submit

    // System state
    hasOrganization: !!user?.organization.id,
  };
};
